import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';

import { PlatformApp } from '@typings';
import { PATH } from '@constants';
import { cn } from '@utils';

import { Icon, Link, Render } from '@components';
import { Job } from '@components/Ui';

type Props = Partial<
  Pick<PlatformApp.Template, 'name' | 'shortDescription' | 'tags'>
> & {
  title: string;
  link: string;
  icon: React.ReactNode;
  installedAppsCount: number;
  installLink?: string;
};

export const PlatformAppTemplate = ({
  name,
  title,
  icon,
  link,
  shortDescription,
  tags,
  installLink,
  installedAppsCount,
}: Props) => {
  return (
    <div className="relative flex">
      <div className="peer relative z-20 flex min-h-[240px] max-w-full flex-1 flex-col gap-4 overflow-hidden rounded-xl border border-transparent bg-white p-6 transition-colors hover:border-neural-02">
        <div className="relative flex min-w-0 gap-3">
          <Link theme to={link} className="contents">
            {icon}
          </Link>

          <div
            className={cn('flex basis-full flex-col justify-center gap-1', {
              'justify-between': !!shortDescription,
            })}
          >
            <div className="flex items-start justify-between gap-4">
              <Link
                theme
                to={link}
                className="text-h5 leading-[32px] text-black transition-colors hover:text-primary"
              >
                {title}
              </Link>

              <Render if={installedAppsCount > 0}>
                <Link
                  theme
                  to={`${PATH.APPS_INSTALLED}/${name}`}
                  className="inline-flex shrink-0 items-center rounded-full bg-primary-subtle px-3 py-1 text-[12px] leading-[14px]"
                >
                  Installed ({installedAppsCount})
                </Link>
              </Render>
            </div>

            <Render if={shortDescription}>
              <p className="-translate-y-1 text-body-medium text-neural-04">
                {shortDescription}
              </p>
            </Render>
          </div>
        </div>

        <Render if={tags}>
          <Job.Tags restrictionAmount={3} tags={tags} />
        </Render>

        <div className="mt-auto flex gap-3">
          <Link
            className="h-10 flex-1 capitalize"
            variant="secondary"
            to={link}
          >
            Explore
            <Icon size="lg" icon={faArrowRight} />
          </Link>

          <Render if={installedAppsCount > 0}>
            <Link
              className="h-10 flex-1 capitalize"
              variant="secondary"
              to={`${PATH.APPS_INSTALLED}/${name}`}
            >
              Show All
            </Link>
          </Render>

          <Render if={installLink}>
            <Link className="h-10 flex-1 capitalize" to={installLink!}>
              Install
            </Link>
          </Render>
        </div>
      </div>

      <Render if={installedAppsCount > 0}>
        <div className="absolute -bottom-1 -right-1 z-10 h-full w-full rounded-xl bg-neural-03/30" />
        <div className="absolute -bottom-2 -right-2 z-10 h-full w-full rounded-xl bg-neural-03/20" />
      </Render>
    </div>
  );
};
